import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, Row, Col, message } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { Package, PackageType, PackageStatus, DeliveryService } from '@/types/residents/package';
import { ResidentSelector } from '@/components/common/ResidentSelector';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import { formatFirestoreTimestamp } from '@/utils/date';

const { Option } = Select;
const { TextArea } = Input;

type HostType = 'resident' | 'employee';

interface PackageModalProps {
  open: boolean;
  package?: Package | null;
  onSuccess: (data: Omit<Package, 'id'>) => void;
  onCancel: () => void;
}

interface FormValues {
  type: PackageType;
  status: PackageStatus;
  trackingNumber: string;
  deliveryService: DeliveryService;
  hostType: HostType;
  residentId?: string;
  employeeId?: string;
  receivedAt: Dayjs;
  collectedAt?: Dayjs;
  expiryDate?: Dayjs;
  description: string;
  senderName?: string;
  senderPhone?: string;
  notes?: string;
}

const PackageModal: React.FC<PackageModalProps> = ({
  open,
  package: packageData,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation(['packages', 'common']);
  const [form] = Form.useForm<FormValues>();
  const [hostType, setHostType] = useState<HostType>('resident');
  const [selectedResident, setSelectedResident] = useState<{ id: string; name: string } | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (open && packageData) {
      // Determine host type based on existing data
      let hostTypeValue: HostType = 'resident';
      if (packageData.employee) {
        hostTypeValue = 'employee';
      }

      form.setFieldsValue({
        ...packageData,
        receivedAt: packageData.receivedAt ? dayjs(formatFirestoreTimestamp(packageData.receivedAt)) : undefined,
        collectedAt: packageData.collectedAt ? dayjs(formatFirestoreTimestamp(packageData.collectedAt)) : undefined,
        expiryDate: packageData.expiryDate ? dayjs(formatFirestoreTimestamp(packageData.expiryDate)) : undefined,
        hostType: hostTypeValue,
        residentId: packageData.resident?.id,
        employeeId: packageData.employee?.id,
        senderName: packageData.sender?.name,
        senderPhone: packageData.sender?.phone,
      });
      setHostType(hostTypeValue);
      
      // Set the selected data
      if (packageData.resident) {
        setSelectedResident(packageData.resident);
      }
      if (packageData.employee) {
        setSelectedEmployee(packageData.employee);
      }
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({
        receivedAt: dayjs(),
        status: 'pending',
        type: 'regular',
        deliveryService: 'other',
        hostType: 'resident',
      });
      setHostType('resident');
      setSelectedResident(null);
      setSelectedEmployee(null);
    }
  }, [open, packageData, form]);

  const handleHostTypeChange = (value: HostType) => {
    setHostType(value);
    // Clear the other fields when switching
    form.setFieldsValue({
      residentId: undefined,
      employeeId: undefined,
    });
    setSelectedResident(null);
    setSelectedEmployee(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const data: Omit<Package, 'id'> = {
        type: values.type,
        status: values.status,
        trackingNumber: values.trackingNumber,
        deliveryService: values.deliveryService,
        receivedAt: values.receivedAt.toDate(),
        collectedAt: values.collectedAt?.toDate(),
        expiryDate: values.expiryDate?.toDate(),
        description: values.description,
        sender: values.senderName ? {
          name: values.senderName,
          phone: values.senderPhone,
        } : undefined,
        notes: values.notes,
      };

      // Only add fields that are not undefined
      if (values.hostType === 'resident' && values.residentId && selectedResident) {
        data.resident = selectedResident;
      }
      
      if (values.hostType === 'employee' && values.employeeId && selectedEmployee) {
        data.employee = selectedEmployee;
      }

      onSuccess(data);
    } catch (error) {
      message.error(t('common:error'));
    }
  };

  const renderHostSelector = () => {
    switch (hostType) {
      case 'resident':
        return (
          <Form.Item
            name="residentId"
            label={t('packages:resident')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <ResidentSelector onResidentDataChange={setSelectedResident} />
          </Form.Item>
        );
      case 'employee':
        return (
          <Form.Item
            name="employeeId"
            label={t('packages:employee')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <EmployeeSelector onEmployeeDataChange={setSelectedEmployee} />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      title={packageData ? t('packages:editPackage') : t('packages:addPackage')}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText={packageData ? t('common:actions.update') : t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      destroyOnHidden
      width={800}
    >
      <Form form={form} layout="vertical" style={{ paddingTop: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('packages:type')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select>
                                        <Select.Option value="regular">{t('packages:types.regular')}</Select.Option>
            <Select.Option value="express">{t('packages:types.express')}</Select.Option>
            <Select.Option value="fragile">{t('packages:types.fragile')}</Select.Option>
            <Select.Option value="oversized">{t('packages:types.oversized')}</Select.Option>
            <Select.Option value="large">{t('packages:types.large')}</Select.Option>
            <Select.Option value="other">{t('packages:types.other')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('packages:status')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select>
                <Option value="pending">{t('packages:statuses.pending')}</Option>
                <Option value="received">{t('packages:statuses.received')}</Option>
                <Option value="notified">{t('packages:statuses.notified')}</Option>
                <Option value="collected">{t('packages:statuses.collected')}</Option>
                <Option value="expired">{t('packages:statuses.expired')}</Option>
                <Option value="returned">{t('packages:statuses.returned')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="trackingNumber"
              label={t('packages:trackingNumber')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('packages:trackingNumberPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="deliveryService"
              label={t('packages:deliveryService')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select>
                <Option value="fedex">{t('packages:deliveryServices.fedex')}</Option>
                <Option value="ups">{t('packages:deliveryServices.ups')}</Option>
                <Option value="dhl">{t('packages:deliveryServices.dhl')}</Option>
                <Option value="usps">{t('packages:deliveryServices.usps')}</Option>
                <Option value="amazon">{t('packages:deliveryServices.amazon')}</Option>
                <Option value="other">{t('packages:deliveryServices.other')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="hostType"
              label={t('packages:hostType')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select onChange={handleHostTypeChange}>
                <Option value="resident">{t('packages:hostTypes.resident')}</Option>
                <Option value="employee">{t('packages:hostTypes.employee')}</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {renderHostSelector()}
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="receivedAt"
              label={t('packages:receivedAt')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="collectedAt"
              label={t('packages:collectedAt')}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="expiryDate"
              label={t('packages:expiryDate')}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label={t('packages:description')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <TextArea rows={3} placeholder={t('packages:descriptionPlaceholder')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="senderName"
              label={t('packages:senderName')}
            >
              <Input placeholder={t('packages:senderNamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="senderPhone"
              label={t('packages:senderPhone')}
            >
              <Input placeholder={t('packages:senderPhonePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label={t('packages:notes')}
        >
          <TextArea rows={3} placeholder={t('packages:notesPlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PackageModal; 